import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import CustomTextArea from '../../../components/text-area/textArea';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import { usePostBrokerSsoConfigurationKey, useWealthlaneIdentityApi } from '../../../hooks';
import { type ModalStatusType } from '../../../types/common';
import _ from 'lodash';
import InitialValuesForm from '../../../constants/initialValues';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import { BrokerSsoConfigurationKeyService } from '../../../services/wealthlane-identity-services/services/BrokerSsoConfigurationKeyService';
interface AddKeyManagementModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  id?: string;
  initialData?: any;
  modalStatus?: ModalStatusType;
  setModalStatus?: (status: ModalStatusType) => void;
  brokerSsoConfigurationId?: string;
}

const AddKeyManagementModal = ({
  isModalOpen,
  closeModal,
  id,
  initialData,
  modalStatus,
  setModalStatus,
  brokerSsoConfigurationId,
}: AddKeyManagementModalType): JSX.Element => {
  const [secretsRevealed, setSecretsRevealed] = useState(false);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [publicKeyCopied, setPublicKeyCopied] = useState(false);
  const baselineKeysRef = useRef({ publicKey: '', privateKey: '' });
  const secretsCacheRef = useRef<{ publicKey?: string | null; privateKey?: string | null } | null>(
    null
  );

  useEffect(() => {
    if (!isModalOpen) {
      setSecretsRevealed(false);
      return;
    }
    if (modalStatus !== 'View') {
      setSecretsRevealed(false);
      return;
    }
    if (initialData?.data == null) {
      return;
    }
    setSecretsRevealed(false);
    secretsCacheRef.current = null;
    baselineKeysRef.current = {
      publicKey: String(initialData.data.publicKey ?? ''),
      privateKey: String(initialData.data.privateKey ?? ''),
    };
  }, [isModalOpen, modalStatus, initialData]);

  const { handleRequest } = useWealthlaneIdentityApi();
  const { mutate, isLoading } = usePostBrokerSsoConfigurationKey(
    handleRequest,
    () => {
      closeModal();
    },
    initialData?.id
  );

  return (
    <Formik
      enableReinitialize
      initialValues={
        initialData && modalStatus !== 'Add' ? initialData.data : InitialValuesForm.addKeyManagement
      }
      //   validationSchema={BROKER_SSO_CONFIGURATION_VALIDATION}
      onSubmit={(values) => {
        mutate({
          brokerSsoConfigurationId: brokerSsoConfigurationId ?? '',
          publicKey: values.publicKey,
          privateKey: values.privateKey,
          effectiveFrom: values.effectiveFrom,
          expiresAt: values.expiresAt !== '' ? values.expiresAt : null,
        });
      }}>
      {({ touched, handleChange, errors, values, setFieldValue, setFieldTouched }) => {
        const handleCopyPublicKey = (): void => {
          const key = secretsRevealed
            ? (secretsCacheRef.current?.publicKey ?? values.publicKey)
            : values.publicKey;
          if (!key) return;
          void navigator.clipboard.writeText(key).then(() => {
            setPublicKeyCopied(true);
            setTimeout(() => {
              setPublicKeyCopied(false);
            }, 2000);
          });
        };

        const handlePrivateKeyEyeClick = async (): Promise<void> => {
          if (modalStatus !== 'View') return;

          if (secretsRevealed) {
            setFieldValue('publicKey', baselineKeysRef.current.publicKey);
            setFieldValue('privateKey', baselineKeysRef.current.privateKey);
            setSecretsRevealed(false);
            return;
          }

          if (secretsCacheRef.current != null) {
            setFieldValue('publicKey', secretsCacheRef.current.publicKey ?? '');
            setFieldValue('privateKey', secretsCacheRef.current.privateKey ?? '');
            setSecretsRevealed(true);
            return;
          }

          if (initialData?.id == null || initialData.id === '') return;

          setSecretsLoading(true);
          try {
            const data = await handleRequest(
              BrokerSsoConfigurationKeyService.getApiIdentityApiAppBrokerSsoConfigurationKeySigningKeySecrets(
                initialData.id
              )
            );
            if (data != null) {
              secretsCacheRef.current = {
                publicKey: data.publicKey,
                privateKey: data.privateKey,
              };
              setFieldValue('publicKey', data.publicKey ?? '');
              setFieldValue('privateKey', data.privateKey ?? '');
              setSecretsRevealed(true);
            }
          } finally {
            setSecretsLoading(false);
          }
        };

        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={isLoading}
            size='md'
            valueEdited={modalStatus !== 'Add' && _.isEqual(values, initialData.data)}
            title={`${modalStatus} Key Management`}
            id={id}
            children={
              <Form id='brokerageSsoConfigurationForm'>
                <ModalBody id='modalBody'>
                  <CustomTextArea
                    label='Public Key'
                    name='publicKey'
                    id='publicKey'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    inputType={modalStatus === 'View' ? 'View' : undefined}
                    disabled={modalStatus !== 'Add'}
                    rows='4'
                    required
                    labelSuffix={
                      modalStatus === 'View' ? (
                        <button
                          type='button'
                          title={publicKeyCopied ? 'Copied!' : 'Copy public key'}
                          className='material-symbols-outlined password-icon cursor-pointer border-0 bg-transparent p-0 align-middle text-lg leading-none'
                          onClick={handleCopyPublicKey}
                          aria-label='Copy public key'>
                          {publicKeyCopied ? 'check' : 'content_copy'}
                        </button>
                      ) : undefined
                    }
                  />
                  <CustomTextArea
                    label='Private Key'
                    name='privateKey'
                    id='privateKey'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    inputType={modalStatus === 'View' ? 'View' : undefined}
                    disabled={modalStatus !== 'Add'}
                    rows='4'
                    required
                    labelSuffix={
                      modalStatus === 'View' ? (
                        <button
                          type='button'
                          disabled={secretsLoading}
                          title={
                            secretsLoading
                              ? 'Loading…'
                              : secretsRevealed
                              ? 'Click to hide key'
                              : 'Click to view key'
                          }
                          className='material-symbols-outlined password-icon cursor-pointer border-0 bg-transparent p-0 align-middle text-lg leading-none disabled:cursor-wait disabled:opacity-50'
                          onClick={() => {
                            void handlePrivateKeyEyeClick();
                          }}
                          aria-label={
                            secretsRevealed
                              ? 'Show key values from list'
                              : 'Load full signing key from server'
                          }>
                          {`visibility${secretsRevealed ? '' : '_off'}`}
                        </button>
                      ) : undefined
                    }
                  />
                  {modalStatus !== 'View' && (
                    <>
                      <CustomDatePicker
                        label='Effective From'
                        name='effectiveFrom'
                        id='effectiveFrom'
                        onChange={handleChange}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        errors={errors}
                        touched={touched}
                        errorValue={errors?.effectiveFrom}
                        touchedValue={touched?.effectiveFrom}
                        arrValue={values?.effectiveFrom}
                        // values={values}
                        defaultValue={new Date()}
                        fieldLabelName='test'
                        required
                      />
                      <CustomDatePicker
                        label='Expires At'
                        name='expiresAt'
                        id='expiresAt'
                        onChange={handleChange}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        errors={errors}
                        touched={touched}
                        errorValue={errors?.expiresAt}
                        touchedValue={touched?.expiresAt}
                        arrValue={values?.expiresAt}
                        // values={values}
                        defaultValue={new Date()}
                        fieldLabelName='test'
                        required
                      />
                    </>
                  )}
                </ModalBody>
                <ModalFooterComponent
                  id='brokerageSsoConfigurationModalFooter'
                  modalStatus={modalStatus === 'View' ? 'History' : modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={modalStatus !== 'Add' && _.isEqual(values, initialData.data)}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default AddKeyManagementModal;
